import is from 'is';
import snakeCase from 'to-snake-case';

/**
 * Regex to extract the contents of a data string.
 */
export const dataRegex = /(?<=data\s*\{)([^}]+)\s*\}/;

/**
 * Create a string representing a contract parenthesis based on the provided items.
 *
 * If the items passed are not strings, or are empty strings, then a blank string will be returned.
 * @param  {...string} items
 * @returns {string} Either an empty string, or the strings enclosed in parenthesis.
 */
export function makeQueryContract(...items) {
  const contract = [];
  for (const item of items) {
    if (!is.string(item)) {
      continue;
    }
    if (is.empty(item)) {
      continue;
    }
    contract.push(item);
  }
  if (is.empty(contract)) {
    return '';
  }
  return `(${contract.join(', ')})`;
}
/**
 * Creates a list of property names of both camel-case and snake-case.
 *
 * If any items passed in are not strings, or are empty strings, they will be ignored.
 *
 * The return is guaranteed to be unique strings.
 *
 * @param  {...string} properties Creates a list of property name, converting any names from camel-case
 * to snake-case.
 * @returns {string[]} The list of property names in standard and snake-case.
 */
export function createAcceptedProperties(...properties) {
  const filtered = properties.filter((property) => (is.string(property) && !is.empty(property)));
  const combined = filtered.concat(filtered.map((property) => snakeCase(property)));
  return Array.from(new Set(combined));
}

/**
 * Filters an array of objects based on the provided criteria object.
 *
 * If the criteria object is empty or not provided, the original array is returned unfiltered.
 * For each key-value pair in the criteria, the function checks if the key exists in the objects and the values match.
 *
 * @param {Object[]} data The array of objects to filter.
 * @param {Object} criteria An object containing key-value pairs to match against the data.
 * Keys not present in the data will be ignored.
 * @returns {Object[]} A filtered array of objects that match all the given criteria.
 */

export function filterData(data, criteria) {
  // Return all data if no criteria are provided
  if (!criteria || Object.keys(criteria).length === 0) {
    return data;
  }

  // Filter the data based on criteria
  return data.filter((item) => {
    return Object.entries(criteria).every(([key, value]) => {
      // Match only if value exists in the item's key
      return (key in item && item[key] === value);
    });
  });
}

// @TODO: Consider breaking this function out into smaller, more focused functions
/**
 * Generic function to fetch items in batches using pagination, yielding each batch as it is fetched
 * @param {*} criteria
 * @param {*} options
 * @param {*} returnProperties
 * @param {Function} getFn - The getter function (getDevices, getStreams, etc.)
 * @param {AbortSignal} [signal] - Optional AbortSignal; the loop stops before each page request when aborted.
 */
export async function* fetchAllItems(criteria = {}, options = {}, returnProperties = [], getFn, signal) {
  let currentPage = 1;
  let totalRecords = null;
  let countPerPage = (options.pagination && options.pagination.count_per_page) || 50;
  let pagination = { count_per_page: countPerPage, start_on_page: currentPage };
  let firstRun = true;
  let fetched = 0;

  while (firstRun || (fetched < totalRecords)) {
    if (signal?.aborted) break;
    firstRun = false;
    const { query, execute } = getFn(null, criteria, { ...options, pagination });
    if (Array.isArray(returnProperties) && returnProperties.length > 0) {
      query.addReturnProperties(...returnProperties);
    }
    const res = await execute();
    const itemsData = Array.isArray(res?.data) ? res.data : [];
    const paginationInfo = res?.pagination || res?.data?.pagination;
    yield itemsData; // Yield this batch immediately to populate table faster
    fetched += itemsData.length;
    if (paginationInfo) {
      totalRecords = paginationInfo.total_records;
      countPerPage = paginationInfo.count_per_page;
    }
    if (!paginationInfo || !paginationInfo.total_pages) break;
    currentPage++;
    pagination.start_on_page = currentPage;
    if (currentPage > paginationInfo.total_pages) break;
  }
}